<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ExternalLink } from "lucide-vue-next";
import { api } from "@/api/client";
import {
  ApiError,
  ConflictError,
  ForbiddenError,
  type AdminCredentialsResponse,
  type Playground,
  type PlaygroundStatus,
  type PlaygroundStatusResponse,
  type SeqCredentialsResponse,
} from "@/api/types";
import { authStore } from "@/auth/authStore";
import { pushToast } from "@/composables/useToasts";
import { copyText } from "@/composables/useClipboard";
import { usePolling } from "@/composables/usePolling";
import { useTabTitle } from "@/composables/useTabTitle";
import { useFaviconDot } from "@/composables/useFaviconDot";
import { notify, ensurePermission } from "@/composables/useNotifications";
import { formatElapsed } from "@/composables/useTime";
import StatusPill from "@/components/StatusPill.vue";
import Stepper, { type Step, type StepState } from "@/components/Stepper.vue";
import LogViewer from "@/components/LogViewer.vue";
import Dialog from "@/components/Dialog.vue";
import Spinner from "@/components/Spinner.vue";

const route = useRoute();
const router = useRouter();

const customer = computed(() => String(route.params.slug));

const playground = ref<Playground | null>(null);
const loadingMeta = ref(true);
const metaError = ref<string | null>(null);

const status = ref<PlaygroundStatusResponse | null>(null);
const logLines = ref<string[]>([]);
const logCursor = ref<number>(0);
// Track which job execution the cursor + logLines belong to. The cursor
// is a byte-offset into a SPECIFIC append-log blob keyed by jobId — when
// the user clicks Retry (or the orchestrator otherwise allocates a new
// jobId), we need to reset both refs or we silently suppress the new
// blob's lines until they exceed the old blob's final size.
const logJobId = ref<string | undefined>(undefined);

// Labels correspond 1:1 to the orchestrator's [step N/20] markers emitted
// from src/orchestrator/runDeploy.ts. Keep these aligned — the tracker
// extracts the integer prefix and the UI looks up STEP_LABELS[N-1] to
// render the row. Sub-letter markers (1b, 7a, 9a/b/c, 10b) are folded
// into their parent ordinal in the tracker (jobs/tracker.ts:142).
//
// Steps 13-20 are the post-deploy phases (previously emitted as
// undifferentiated [post] lines, rolled up into a synthetic post-deploy
// row). Each headline action in post-deploy now has its own
// [step N/20] marker so the progress list reflects which phase is active.
// Intermediate activities within a step (e.g. encryption-keyphrase
// insert during step 13) stay as [post] lines visible in the log panel.
const STEP_LABELS = [
  "Shared infrastructure",      // 1:  SharedTrialInfra outputs + ASP slot allocation (1b)
  "Seed Seq admin password",    // 2:  seedSeqAdminPassword → KV + seqcli config hash
  "Customer infrastructure",    // 3:  SQL DBs, sidecar ACIs, Seq site, KV cert
  "Fetch encryption secrets",   // 4:  pull passphrase from KV
  "Download binaries",          // 5:  sharedo.zip + eventEngine.zip from sharedoreleases
  "Resolve config values",      // 6:  write trial-overrides.json
  "Generate customer config",   // 7:  variables.json + targets + OIDC client secrets (7a)
  "Capture OIDC secrets",       // 8:  read back generated client secrets into KV
  "Configure binaries",         // 9:  ConfigurationTool + build_configure + trial-debug (9a/b/c)
  "Upload + signing cert",      // 10: zip per-app + upload + provision signing cert (10b)
  "App Services + DNS",         // 11: TrialCustomerApp — App Services, DNS, hostname bindings
  "Verify App Service ready",   // 12: SCM endpoint check + VNet integration self-heal
  "Database setup",             // 13: DB migrate + encryption.keyPhrase row + EE config import
  "Document management",        // 14: AzureBlob plugin + 13-repo seed + Prizm preview config
  "Restart & verify",           // 15: restart all 3 App Services + post-restart health probe
  "Reset runtime config",       // 16: resetConfig.ps1 + reset-cache-key.ps1
  "Clear Seq first-login flag", // 17: Seq /api/users/login + newPassword to clear must-change
  "Seed admin user",            // 18: seedAdminUser — opt-in admin upsert + wipe demo users (task AL)
  "Preconfigure environment",   // 19: applyStartupWizard — replay the operator's wizard answers (task BP)
  "Rebuild search indexes",     // 20: clearElasticIndexes.ps1 — full ES reindex, LAST so it indexes
                                //     the content the wizard imported at 19
];

async function loadMeta() {
  loadingMeta.value = true;
  metaError.value = null;
  try {
    playground.value = await api.getPlayground(customer.value);
  } catch (e) {
    metaError.value = e instanceof Error ? e.message : "Failed to load playground.";
  } finally {
    loadingMeta.value = false;
  }
}

const polling = usePolling<PlaygroundStatusResponse>({
  fn: async () => api.getStatus(customer.value, logCursor.value),
  onResult: (s) => {
    const prevStatus = status.value?.status;
    status.value = s;

    // jobId-change reset: the cursor + logLines we've accumulated are
    // anchored to a SPECIFIC append-log blob (server-side). When a new
    // jobId appears (Retry, fresh deploy after Destroy, etc.) we need
    // to throw both away and start fresh against the new blob. Without
    // this, the old cursor would suppress every push from the new blob
    // until that blob grew past the OLD blob's final size — the bug
    // that surfaced "blank logs after retry."
    if (s.jobId && s.jobId !== logJobId.value) {
      logJobId.value = s.jobId;
      logCursor.value = 0;
      logLines.value = [];
    }

    // The /status endpoint respects ?since=<cursor> and returns ONLY new
    // content beyond that offset (capped to the last 200 lines on the
    // first poll where since=0). So recentLog is always safe to append
    // verbatim — no overlap with what we already have. Push only when
    // there's content AND the cursor moved.
    if (s.recentLog?.length && (s.logCursor ?? 0) > (logCursor.value ?? 0)) {
      logLines.value.push(...s.recentLog);
    }
    if (s.logCursor != null) logCursor.value = s.logCursor;
    // Refresh meta on terminal transition (to update lastJob, fqdn reachability).
    const terminal = s.status === "ready" || s.status === "failed";
    const wasInFlight = prevStatus === "provisioning" || prevStatus === "destroying";
    if (terminal && wasInFlight) {
      void loadMeta();
      if (s.status === "ready") {
        notify(`${customer.value} ready · operate-playground`, `Deploy complete.`);
      } else {
        notify(`${customer.value} failed · operate-playground`, s.failureSummary ?? "Deploy failed.");
      }
    }
  },
  onError: (e) => {
    // Successful destroys delete the ownership blob, so the next status
    // poll returns 404. If we were destroying when that happened, treat
    // it as success: toast + redirect to dashboard. Without this the UI
    // sits on a "Lost connection. Retrying…" banner forever.
    if (
      e instanceof ApiError &&
      e.status === 404 &&
      status.value?.status === "destroying"
    ) {
      polling.stop();
      pushToast(`${customer.value} destroyed.`, "info");
      void router.push("/");
    }
  },
});

const lostConnection = polling.lostConnection;
const lastSuccessAt = polling.lastSuccessAt;

const stepCount = computed(() => status.value?.totalSteps ?? STEP_LABELS.length);
const currentStep = computed(() => status.value?.step ?? 0);
const completedSet = computed(() => new Set(status.value?.completedSteps ?? []));

const steps = computed<Step[]>(() => {
  const total = stepCount.value;
  const s = status.value;
  const rows: Step[] = Array.from({ length: total }, (_, i) => {
    const ordinal = i + 1;
    const label = STEP_LABELS[i] ?? `Step ${ordinal}`;
    let state: StepState = "pending";
    if (s) {
      if (s.status === "failed" && ordinal === currentStep.value) state = "failed";
      else if (completedSet.value.has(ordinal)) state = "ok";
      else if (s.status === "provisioning" && ordinal === currentStep.value) state = "running";
      else if (s.status === "ready") state = "ok";
      else if (s.status === "failed" && ordinal < currentStep.value) state = "ok";
      else if (s.status === "destroying") state = "skipped";
    }
    return { label, state };
  });

  // Legacy synthetic post-deploy row. Modern orchestrator emits
  // [step 12/16]..[step 16/16] markers for each post-deploy phase, so
  // they appear as real rows above. This synthetic row is only used
  // for old in-flight deploys still running with /11 markers (where
  // post-deploy never advances the step counter past 11 and is
  // signalled by the postPhase flag instead). Once nothing in flight
  // uses /11, this branch is dead code and can be removed.
  if (total === 11 && (s?.postPhase || s?.status === "ready")) {
    const postState: StepState =
      s.status === "ready" ? "ok"
      : s.status === "failed" ? "failed"
      : "running";
    rows.push({ label: "Post-deploy configuration", state: postState });
  }
  return rows;
});

const elapsed = ref("0s");
let elapsedTimer: ReturnType<typeof setInterval> | null = null;
function updateElapsed() {
  const started = playground.value?.lastJob?.startedAt ?? playground.value?.createdAt;
  if (started) elapsed.value = formatElapsed(started);
}

watch(playground, () => {
  updateElapsed();
});
onMounted(() => {
  void loadMeta();
  elapsedTimer = setInterval(updateElapsed, 1000);
  // Prompt for notifications on first detail-view of an in-flight deploy.
  void ensurePermission();
});
import { onUnmounted } from "vue";
onUnmounted(() => {
  if (elapsedTimer) clearInterval(elapsedTimer);
});

useTabTitle(() => {
  const s = status.value;
  if (!s) return null;
  if (s.status === "ready") return `✓ ${customer.value} ready · operate-playground`;
  if (s.status === "failed") return `✗ ${customer.value} failed · operate-playground`;
  if (s.status === "provisioning" || s.status === "destroying") {
    return `${customer.value} · step ${currentStep.value}/${stepCount.value} · operate-playground`;
  }
  return `${customer.value} · operate-playground`;
});

useFaviconDot(() => status.value?.status ?? "idle");

const canDelete = computed(() => {
  const p = playground.value;
  if (!p) return false;
  if (p.status === "provisioning" || p.status === "destroying") return false;
  return (
    authStore.isAdmin.value || p.createdBy.upn === authStore.state.me?.upn
  );
});

const isAdminOverride = computed(
  () =>
    authStore.isAdmin.value &&
    playground.value?.createdBy.upn !== authStore.state.me?.upn,
);

const deleteModalOpen = ref(false);
const deleteConfirm = ref("");
const deleting = ref(false);
const clearing = ref(false);
const retrying = ref(false);
const downloadingLog = ref(false);

function openDelete() {
  deleteConfirm.value = "";
  deleteModalOpen.value = true;
}

/**
 * Clear logs is enabled for the same audience as Delete (creator or
 * admin) but only when there's no live job. The backend re-checks both,
 * but disabling here saves a round-trip + makes intent visible.
 */
const canClearLogs = computed(() => {
  if (!playground.value) return false;
  const s = playground.value.status;
  if (s === "provisioning" || s === "destroying") return false;
  return canDelete.value;
});

const clearLogsTooltip = computed(() => {
  if (!playground.value) return undefined;
  const s = playground.value.status;
  if (s === "provisioning" || s === "destroying") {
    return "Wait for the current job to finish.";
  }
  if (!canDelete.value) {
    return "Only the creator or an admin can clear logs.";
  }
  return "Wipe the previous job log and error before retrying.";
});

const canRetry = computed(() => {
  if (!playground.value) return false;
  // Retry valid from `failed` AND `failed-interrupted`. The latter is
  // what the ownership sweeper writes when a provisioning blob looks
  // orphaned (lease expired + lastJob.startedAt older than 10 min) —
  // typically caused by the UI server restarting mid-deploy, NOT by
  // the orchestrator itself dying (the Container App Job runs in its
  // own env and keeps going). Either way, "re-fire the deploy" is the
  // right action; the server-side guard mirrors this and the runner
  // still rejects if a job is genuinely still active for the slug.
  if (!canDelete.value) return false;
  const s = playground.value.status;
  return s === "failed" || s === "failed-interrupted";
});

async function retry() {
  if (!playground.value) return;
  if (!canRetry.value) return;
  retrying.value = true;
  try {
    await api.retry(playground.value.customer);
    pushToast("Retry started — re-running deploy from the failed step.", "info");
    // Refresh meta + poll so the UI flips from "failed" to
    // "provisioning step 1/11" immediately rather than waiting for the
    // next poll tick.
    await loadMeta();
    void polling.refresh();
  } catch (e) {
    if (e instanceof ForbiddenError) {
      pushToast("Only the creator or an admin can retry this playground.", "error");
    } else if (e instanceof ConflictError) {
      pushToast("Retry is only available from the failed state.", "error");
    } else {
      pushToast(e instanceof Error ? e.message : "Failed to start retry.", "error");
    }
  } finally {
    retrying.value = false;
  }
}

// ── Upgrade ────────────────────────────────────────────────────────────────
//
// "Upgrade" runs the orchestrator in mode="upgrade", which preserves the
// customer's customisations (blob repos, EE config) while allowing version
// swaps. Two flavours via the dialog:
//   - Refresh same version: re-apply orchestrator changes without bumping.
//   - Pick a target version: switch to a different release.
// Server-side compatibility check (versionCompatibility.ts) refuses
// downgrades, cross-tier moves (playground ↔ release), and different
// playground builds.
const upgrading = ref(false);
const showUpgradeDialog = ref(false);
const upgradeTargetVersion = ref("");
const upgradeRefresh = ref(true);
const upgradeCandidates = ref<{ version: string; publishedAt: string }[]>([]);
const upgradeCandidatesLoading = ref(false);

// Cross-tier upgrade (release instance → playground build). Only offered when
// the current instance is on a release ring. Server enforces the direction
// (release → playground only) in versionCompatibility.ts; this just drives the
// dialog. The playground build list is loaded lazily when the operator flips
// the tier toggle.
const upgradeTargetTier = ref<"release" | "playground">("release");
const upgradePlaygroundCandidates = ref<{ version: string; publishedAt: string }[]>([]);
const upgradePlaygroundLoading = ref(false);

// True when the current customer is on a playground build. Playground builds
// have no SemVer ordering — each name is a separately-published blob path
// updated in place. Two valid actions: refresh (re-pull the same name's latest
// content) or switch to a DIFFERENT playground build (re-platform onto another
// experimental build — server allows it and warns about DB-shape compatibility).
// The dialog offers both: the build picker lists the playground builds loaded
// in openUpgradeDialog (ring=playground).
const upgradeIsPlayground = computed(
  () => (playground.value?.ring ?? "") === "playground",
);

// True when the operator is taking a release instance onto a playground build.
// Drives which candidate list we show, the warning note, and the submit payload.
const crossTierToPlayground = computed(
  () => !upgradeIsPlayground.value && upgradeTargetTier.value === "playground",
);

// True when the chosen upgrade target is a playground build — either a release
// instance crossing tiers, or a playground instance switching to another build.
// Drives the "Target build" labelling, the build candidate list, and the warning.
const targetIsPlaygroundBuild = computed(
  () => upgradeIsPlayground.value || crossTierToPlayground.value,
);

// Lazily fetch the playground build names the first time the operator flips the
// tier toggle to "playground" (release customers only).
async function loadPlaygroundCandidates(): Promise<void> {
  if (upgradePlaygroundCandidates.value.length > 0 || upgradePlaygroundLoading.value) return;
  upgradePlaygroundLoading.value = true;
  try {
    const resp = await api.listSharedoVersions("playground");
    upgradePlaygroundCandidates.value = resp.versions ?? [];
  } catch (e) {
    pushToast(e instanceof Error ? e.message : "Failed to load playground builds.", "error");
  } finally {
    upgradePlaygroundLoading.value = false;
  }
}

function onUpgradeTierChange(): void {
  upgradeTargetVersion.value = "";
  if (upgradeTargetTier.value === "playground") {
    // No "refresh" semantics across tiers — you must pick a build.
    upgradeRefresh.value = false;
    void loadPlaygroundCandidates();
  } else {
    upgradeRefresh.value = true;
  }
}

// Simple SemVer-ish comparator. Mirrors src/lib/versionCompatibility.ts's
// compareSemver but inlined here so the Vue bundle doesn't have to reach
// across the tsconfig boundary into the server's src/. Numeric tuple
// compare; non-numeric segments compare as 0 (so "7.11" == "7.11.0").
function compareSemverShort(a: string, b: string): number {
  const parse = (s: string) =>
    s.replace(/^v/i, "").split(/[-+]/)[0]!.split(".").map((n) => Number.parseInt(n, 10) || 0);
  const av = parse(a);
  const bv = parse(b);
  const len = Math.max(av.length, bv.length);
  for (let i = 0; i < len; i++) {
    const ai = av[i] ?? 0;
    const bi = bv[i] ?? 0;
    if (ai !== bi) return ai - bi;
  }
  return 0;
}

// Candidates the user can actually choose. For release-ring customers we
// strip anything older than current (downgrades would 400 server-side
// anyway). Playground instances don't use this list — displayedUpgradeCandidates
// shows their build names raw (no SemVer filter) — but keep a same-name filter
// here so any accidental reuse can't regress to cross-name builds.
const filteredUpgradeCandidates = computed(() => {
  const cur = playground.value?.sharedoVersion ?? "";
  if (upgradeIsPlayground.value) {
    return upgradeCandidates.value.filter((v) => v.version === cur);
  }
  // Release ring: keep entries >= current. `>=` (not `>`) because same-
  // version IS the "refresh" path — but in the dropdown that entry is
  // rendered disabled with the "(current)" suffix the option logic
  // already provides, so the user can't accidentally submit it.
  return upgradeCandidates.value.filter(
    (v) => compareSemverShort(v.version, cur) >= 0,
  );
});

// The list actually rendered in the dropdown. For a cross-tier move to
// playground we show the raw playground build names — they have no SemVer
// ordering, so they must NOT pass through compareSemverShort (which parses a
// name to 0 and would drop everything). Otherwise the existing release filter.
const displayedUpgradeCandidates = computed(() => {
  // Playground instance: openUpgradeDialog already loaded the build names with
  // ring=playground, so show them raw (no SemVer filter — names parse to 0).
  if (upgradeIsPlayground.value) return upgradeCandidates.value;
  // Release instance crossing to playground: the lazily-loaded build list.
  // Otherwise the SemVer-filtered release list.
  return crossTierToPlayground.value
    ? upgradePlaygroundCandidates.value
    : filteredUpgradeCandidates.value;
});

const displayedCandidatesLoading = computed(() =>
  crossTierToPlayground.value ? upgradePlaygroundLoading.value : upgradeCandidatesLoading.value,
);

const canUpgrade = computed(() => {
  if (!playground.value) return false;
  if (!canDelete.value) return false;
  return playground.value.status === "ready";
});

async function openUpgradeDialog() {
  if (!playground.value) return;
  showUpgradeDialog.value = true;
  upgradeRefresh.value = true;
  upgradeTargetVersion.value = "";
  upgradeTargetTier.value = "release";
  upgradePlaygroundCandidates.value = [];
  upgradeCandidates.value = [];
  upgradeCandidatesLoading.value = true;
  try {
    // Use the SAME endpoint as the create form. Filter at the dialog
    // level by ring (release vs playground); the server enforces the
    // strict rules (no downgrades, etc.) when the user submits.
    const currentRing = playground.value.ring ?? "staging";
    const ringForListing = currentRing === "playground" ? "playground" : "staging";
    const resp = await api.listSharedoVersions(ringForListing);
    upgradeCandidates.value = resp.versions ?? [];
  } catch (e) {
    pushToast(e instanceof Error ? e.message : "Failed to load versions.", "error");
  } finally {
    upgradeCandidatesLoading.value = false;
  }
}

function closeUpgradeDialog() {
  showUpgradeDialog.value = false;
}

async function confirmUpgrade() {
  if (!playground.value) return;
  // Defensive: the submit button is supposed to be disabled when this
  // would produce an empty payload, but the v-model timing on the
  // checkbox + select means we sometimes saw `{ version: "" }` reach
  // the server (which 400s with "Upgrade requires either …"). Belt-
  // and-braces: short-circuit here too.
  if (!upgradeRefresh.value && !upgradeTargetVersion.value) {
    pushToast(
      targetIsPlaygroundBuild.value
        ? "Pick a playground build to switch to, or check 'Refresh' to re-pull the current build."
        : "Pick a target version, or check 'Refresh same version'.",
      "error",
    );
    return;
  }
  upgrading.value = true;
  try {
    // Build payload explicitly to avoid sending an empty `version: ""`
    // which the server rejects. A cross-tier move to playground carries the
    // target ring so the server (and orchestrator) switch tiers.
    const payload: { refresh?: boolean; version?: string; ring?: string } =
      crossTierToPlayground.value
        ? { version: upgradeTargetVersion.value, ring: "playground" }
        : upgradeRefresh.value
          ? { refresh: true }
          : { version: upgradeTargetVersion.value };
    const result = await api.upgrade(playground.value.customer, payload);
    pushToast(
      `Upgrade started — ${result.fromVersion} → ${result.toVersion}.`,
      "info",
    );
    showUpgradeDialog.value = false;
    await loadMeta();
    void polling.refresh();
  } catch (e) {
    if (e instanceof ForbiddenError) {
      pushToast("Only the creator or an admin can upgrade this playground.", "error");
    } else if (e instanceof ConflictError) {
      pushToast(
        e instanceof Error ? e.message : "Upgrade not allowed in current state.",
        "error",
      );
    } else {
      pushToast(e instanceof Error ? e.message : "Failed to start upgrade.", "error");
    }
  } finally {
    upgrading.value = false;
  }
}

async function downloadLog() {
  if (!playground.value) return;
  downloadingLog.value = true;
  try {
    await api.downloadFullLog(playground.value.customer);
  } catch (e) {
    pushToast(e instanceof Error ? e.message : "Failed to download log.", "error");
  } finally {
    downloadingLog.value = false;
  }
}

async function clearLogs() {
  if (!playground.value) return;
  if (!canClearLogs.value) return;
  clearing.value = true;
  try {
    await api.clearLogs(playground.value.customer);
    pushToast("Logs cleared.", "info");
    // Re-fetch ownership + status so the UI drops the previous job's
    // error banner / step ticks / log tail. The polling loop will pick
    // up the empty log blob on its next tick too.
    await loadMeta();
    void polling.refresh();
  } catch (e) {
    if (e instanceof ForbiddenError) {
      pushToast("Only the creator or an admin can clear logs.", "error");
    } else if (e instanceof ConflictError) {
      pushToast("A job is still running — wait for it to finish.", "error");
    } else {
      pushToast(e instanceof Error ? e.message : "Failed to clear logs.", "error");
    }
  } finally {
    clearing.value = false;
  }
}

async function confirmDelete() {
  if (!playground.value) return;
  if (deleteConfirm.value !== playground.value.customer) return;
  deleting.value = true;
  try {
    await api.destroy(playground.value.customer);
    deleteModalOpen.value = false;
    await loadMeta();
    void polling.refresh();
  } catch (e) {
    if (e instanceof ForbiddenError) {
      pushToast(
        "You can only delete playgrounds you created. Ask an admin to delete this one.",
        "error",
      );
    } else if (e instanceof ConflictError) {
      pushToast(`A job is already running for \`${playground.value.customer}\`. Wait for it to finish.`, "error");
    } else if (e instanceof ApiError && e.status >= 500) {
      pushToast(
        "Backend error. The orchestrator may have crashed. Page #trial-ops with the job ID below.",
        "error",
      );
    } else {
      pushToast(e instanceof Error ? e.message : "Failed to delete.", "error");
    }
  } finally {
    deleting.value = false;
  }
}

const statusForPill = computed<PlaygroundStatus | "idle">(
  () => status.value?.status ?? playground.value?.status ?? "idle",
);

// ── Seq credentials panel ─────────────────────────────────────────────
//
// Credentials are fetched lazily on first reveal so the plain-text
// password doesn't ride along with every status poll. Once loaded
// they live in this ref until the page unmounts.
//
// Empty states:
//   - 404 with body `{ error: "seq-credentials-not-provisioned" }`
//     for customers deployed before the per-customer Seq feature.
//     We render a "Re-deploy or Upgrade" hint.
//   - 503 if the UI itself isn't wired to a Key Vault — operator
//     needs to redeploy SharedUi with the env var baked in.
const seqCreds = ref<SeqCredentialsResponse | null>(null);
const seqLoading = ref(false);
const seqError = ref<string | null>(null);
const seqNotProvisioned = ref(false);
const seqPasswordRevealed = ref(false);

async function loadSeqCreds() {
  if (!playground.value) return;
  if (seqCreds.value) return; // already loaded
  seqLoading.value = true;
  seqError.value = null;
  seqNotProvisioned.value = false;
  try {
    seqCreds.value = await api.getSeqCredentials(playground.value.customer);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      // Distinguish "not yet provisioned" from generic 404. Server
      // returns `{ error: "seq-credentials-not-provisioned" }` for
      // older customers; treat any 404 here as that empty state.
      seqNotProvisioned.value = true;
    } else if (e instanceof ApiError && e.status === 503) {
      seqError.value =
        "Seq credentials aren't wired up on this UI deployment. Ask an admin to redeploy SharedUi.";
    } else {
      seqError.value = e instanceof Error ? e.message : "Failed to load Seq credentials.";
    }
  } finally {
    seqLoading.value = false;
  }
}

async function revealSeqPassword() {
  await loadSeqCreds();
  if (seqCreds.value) {
    seqPasswordRevealed.value = true;
  }
}

function hideSeqPassword() {
  seqPasswordRevealed.value = false;
}

async function copySeqPassword() {
  // Only hit the network if we don't already hold the secret — copying a
  // value we already have keeps the clipboard write inside the click's
  // activation window and avoids the "Document is not focused" reject.
  if (!seqCreds.value) await loadSeqCreds();
  if (!seqCreds.value) return;
  try {
    await copyText(seqCreds.value.password);
    pushToast("Seq password copied to clipboard.", "info");
  } catch {
    seqPasswordRevealed.value = true;
    pushToast(
      "Couldn't copy automatically — the password is now shown so you can copy it manually.",
      "error",
    );
  }
}

// ── Operate admin login credentials panel ─────────────────────────────
//
// Mirrors the Seq panel exactly — lazy-fetch on first reveal so the
// plain-text password never rides along with the regular status poll.
//
// Empty / error states:
//   - 404 with body `{ error: "admin-credentials-not-provisioned" }` for
//     customers deployed without `seedAdminUser: true` (the default —
//     newer playground builds own first-admin via StartupWizard). We
//     render a "Re-deploy with admin seeding enabled" hint.
//   - 503 if the UI itself isn't wired to a Key Vault — operator needs
//     to redeploy SharedUi.
//
// We always render the panel (rather than gating on a per-customer flag);
// the 404 empty state IS the "not provisioned" hint, which is cheaper than
// plumbing seedAdminUser through the status response.
const adminCreds = ref<AdminCredentialsResponse | null>(null);
const adminLoading = ref(false);
const adminError = ref<string | null>(null);
const adminNotProvisioned = ref(false);
const adminPasswordRevealed = ref(false);

async function loadAdminCreds() {
  if (!playground.value) return;
  if (adminCreds.value) return; // already loaded
  adminLoading.value = true;
  adminError.value = null;
  adminNotProvisioned.value = false;
  try {
    adminCreds.value = await api.getAdminCredentials(playground.value.customer);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) {
      adminNotProvisioned.value = true;
    } else if (e instanceof ApiError && e.status === 503) {
      adminError.value =
        "Admin credentials aren't wired up on this UI deployment. Ask an admin to redeploy SharedUi.";
    } else {
      adminError.value = e instanceof Error ? e.message : "Failed to load admin credentials.";
    }
  } finally {
    adminLoading.value = false;
  }
}

async function revealAdminPassword() {
  await loadAdminCreds();
  if (adminCreds.value) {
    adminPasswordRevealed.value = true;
  }
}

function hideAdminPassword() {
  adminPasswordRevealed.value = false;
}

async function copyAdminPassword() {
  // Only hit the network if we don't already hold the secret — copying a
  // value we already have keeps the clipboard write inside the click's
  // activation window and avoids the "Document is not focused" reject.
  if (!adminCreds.value) await loadAdminCreds();
  if (!adminCreds.value) return;
  try {
    await copyText(adminCreds.value.password);
    pushToast("Admin password copied to clipboard.", "info");
  } catch {
    adminPasswordRevealed.value = true;
    pushToast(
      "Couldn't copy automatically — the password is now shown so you can copy it manually.",
      "error",
    );
  }
}

const lostConnectionAge = ref("0s");
let lostTimer: ReturnType<typeof setInterval> | null = null;
onMounted(() => {
  lostTimer = setInterval(() => {
    if (lastSuccessAt.value) {
      const secs = Math.max(0, Math.floor((Date.now() - lastSuccessAt.value) / 1000));
      lostConnectionAge.value = `${secs}s`;
    }
  }, 1000);
});
onUnmounted(() => {
  if (lostTimer) clearInterval(lostTimer);
});
</script>

<template>
  <section class="mx-auto max-w-4xl px-6 py-6 pb-20">
    <nav class="mb-3 text-2xs">
      <router-link to="/" class="text-fg-muted hover:underline">&lt; Playgrounds</router-link>
    </nav>

    <div v-if="loadingMeta && !playground" class="flex items-center gap-2 text-fg-muted">
      <Spinner :size="14" /> Loading…
    </div>

    <div
      v-if="metaError"
      class="mb-3 rounded border border-status-failed/40 bg-status-failed/10 p-3 text-sm text-status-failed"
    >{{ metaError }}<button class="ml-2 underline" @click="loadMeta">retry</button></div>

    <template v-if="playground">
      <header class="mb-4 flex items-center justify-between gap-3">
        <h1 class="mono text-lg font-medium">{{ playground.customer }}</h1>
        <div class="flex gap-2">
          <a
            v-if="playground.status === 'ready'"
            :href="`https://${playground.fqdn}`"
            target="_blank"
            rel="noreferrer noopener"
            class="inline-flex items-center gap-1 rounded border border-border px-3 py-1.5 text-sm hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark"
          ><ExternalLink :size="14" /> Open</a>
          <button
            v-else
            type="button"
            class="inline-flex items-center gap-1 rounded border border-border px-3 py-1.5 text-sm text-fg-muted opacity-60"
            disabled
            :title="playground.status === 'provisioning' ? 'Open available once provisioning completes.' : 'Not reachable.'"
          ><ExternalLink :size="14" /> Open</button>

          <button
            type="button"
            class="inline-flex items-center gap-1 rounded border border-border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!canClearLogs || clearing"
            :title="clearLogsTooltip"
            @click="clearLogs"
          >{{ clearing ? "Clearing…" : "Clear logs" }}</button>

          <button
            v-if="canRetry"
            type="button"
            class="inline-flex items-center gap-1 rounded border border-status-provisioning/50 px-3 py-1.5 text-sm text-status-provisioning hover:bg-status-provisioning/10 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="retrying"
            title="Re-fire the deploy from the last failed step. Orchestrator state (terraform, KV, ASP slot) is preserved — retry picks up where the previous attempt left off."
            @click="retry"
          >{{ retrying ? "Retrying…" : "Retry" }}</button>

          <button
            v-if="canUpgrade"
            type="button"
            class="inline-flex items-center gap-1 rounded border border-border px-3 py-1.5 text-sm hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="upgrading"
            title="Re-deploy the customer with mode=upgrade — preserves customer data, repos and EE config. Use to refresh the same version or bump to a new release."
            @click="openUpgradeDialog"
          >{{ upgrading ? "Upgrading…" : "Upgrade" }}</button>

          <button
            type="button"
            class="rounded border border-status-failed/50 px-3 py-1.5 text-sm text-status-failed hover:bg-status-failed/10 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!canDelete"
            :title="!canDelete ? `Cannot delete in current state.` : undefined"
            @click="openDelete"
          >Delete</button>
        </div>
      </header>

      <p class="mb-2 flex flex-wrap items-center gap-2 text-sm">
        <StatusPill :status="statusForPill" />
        <span v-if="statusForPill === 'provisioning' && playground.lastJob?.kind === 'upgrade'" class="text-fg-muted">
          · upgrading · step {{ currentStep }}/{{ stepCount }} · started {{ elapsed }} ago by
          <span class="mono">{{ playground.createdBy.upn }}</span>
        </span>
        <span v-else-if="statusForPill === 'provisioning' || statusForPill === 'destroying'" class="text-fg-muted">
          · step {{ currentStep }}/{{ stepCount }} · started {{ elapsed }} ago by
          <span class="mono">{{ playground.createdBy.upn }}</span>
        </span>
        <span v-else-if="statusForPill === 'ready'" class="text-fg-muted">
          · ready · created by <span class="mono">{{ playground.createdBy.upn }}</span>
        </span>
        <span v-else-if="statusForPill === 'failed' || statusForPill === 'failed-interrupted'" class="text-fg-muted">
          · failed · created by <span class="mono">{{ playground.createdBy.upn }}</span>
        </span>
      </p>

      <div
        v-if="status?.status === 'failed' && status.failureSummary"
        class="mb-4 rounded border border-status-failed/40 bg-status-failed/10 p-3 text-sm text-status-failed"
      >
        {{ status.failureSummary }} Logs are below. Use Retry to re-fire the deploy without losing state, or Delete to start over.
      </div>

      <dl class="mb-6 grid grid-cols-1 gap-x-8 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]">
        <dt class="text-2xs uppercase tracking-wide text-fg-muted">URL</dt>
        <dd>
          <a
            v-if="playground.status === 'ready'"
            :href="`https://${playground.fqdn}`"
            target="_blank"
            rel="noreferrer noopener"
            class="mono inline-flex items-center gap-1 underline"
          >
            https://{{ playground.fqdn }}
            <ExternalLink :size="12" />
          </a>
          <span v-else class="mono text-fg-muted">
            https://{{ playground.fqdn }}
            <span class="ml-1 text-2xs">(not yet reachable)</span>
          </span>
        </dd>
        <dt class="text-2xs uppercase tracking-wide text-fg-muted">Versions</dt>
        <dd class="text-fg-muted">
          Operate <span class="mono text-fg dark:text-fg-dark">{{ playground.sharedoVersion ?? "—" }}</span>
          · Event Engine <span class="mono text-fg dark:text-fg-dark">{{ playground.eeVersion ?? "—" }}</span>
          · ring <span class="mono text-fg dark:text-fg-dark">{{ playground.ring ?? "—" }}</span>
        </dd>
      </dl>

      <div
        v-if="lostConnection"
        class="mb-4 rounded border border-status-failed/40 bg-status-failed/10 p-2 text-2xs text-status-failed"
      >
        Lost connection. Retrying… (last update {{ lostConnectionAge }} ago)
      </div>

      <h2 class="mb-2 text-2xs font-medium uppercase tracking-wide text-fg-muted">Progress</h2>
      <div class="mb-6 rounded border border-border p-3 dark:border-border-dark">
        <Stepper :steps="steps" />
      </div>

      <h2 class="mb-2 text-2xs font-medium uppercase tracking-wide text-fg-muted">Operate admin login</h2>
      <div class="mb-6 rounded border border-border p-3 dark:border-border-dark">
        <div v-if="adminNotProvisioned" class="text-sm text-fg-muted">
          Admin credentials not provisioned. This playground was deployed without the admin-user seed step
          (newer builds with the StartupWizard own this; older builds need the "Seed admin user" checkbox
          enabled on create or upgrade).
        </div>
        <div
          v-else-if="adminError"
          class="text-sm text-status-failed"
        >{{ adminError }}</div>
        <dl
          v-else
          class="grid grid-cols-1 gap-x-8 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]"
        >
          <dt class="text-2xs uppercase tracking-wide text-fg-muted">URL</dt>
          <dd>
            <a
              v-if="adminCreds || playground.status === 'ready'"
              :href="adminCreds?.url ?? `https://${playground.customer}.${playground.dnsZone}/`"
              target="_blank"
              rel="noreferrer noopener"
              class="mono inline-flex items-center gap-1 underline"
            >
              {{ adminCreds?.url ?? `https://${playground.customer}.${playground.dnsZone}/` }}
              <ExternalLink :size="12" />
            </a>
            <span v-else class="mono text-fg-muted">
              https://{{ playground.customer }}.{{ playground.dnsZone }}/
              <span class="ml-1 text-2xs">(not yet reachable)</span>
            </span>
          </dd>

          <dt class="text-2xs uppercase tracking-wide text-fg-muted">Username</dt>
          <!-- 'admin' is only right for seedAdminUser environments; a
               preconfigured one uses whatever the operator typed into the wizard.
               Prefer the playground payload, which carries the username as a
               non-secret field so it renders immediately - adminCreds only
               arrives once the password has been revealed, which is why this
               used to sit on 'admin' until you clicked. -->
          <dd class="mono">
            {{ playground.adminUsername ?? adminCreds?.username ?? "admin" }}
          </dd>

          <dt class="text-2xs uppercase tracking-wide text-fg-muted">Password</dt>
          <dd class="flex flex-wrap items-center gap-2">
            <span
              v-if="adminPasswordRevealed && adminCreds"
              class="mono break-all"
            >{{ adminCreds.password }}</span>
            <span v-else class="mono text-fg-muted">••••••••••••</span>
            <button
              v-if="!adminPasswordRevealed"
              type="button"
              class="rounded border border-border px-2 py-0.5 text-2xs hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark disabled:opacity-50"
              :disabled="adminLoading"
              @click="revealAdminPassword"
            >{{ adminLoading ? "Loading…" : "Reveal" }}</button>
            <button
              v-else
              type="button"
              class="rounded border border-border px-2 py-0.5 text-2xs hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark"
              @click="hideAdminPassword"
            >Hide</button>
            <button
              type="button"
              class="rounded border border-border px-2 py-0.5 text-2xs hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark disabled:opacity-50"
              :disabled="adminLoading"
              @click="copyAdminPassword"
            >Copy</button>
          </dd>
        </dl>
      </div>

      <h2 class="mb-2 text-2xs font-medium uppercase tracking-wide text-fg-muted">Logs (Seq)</h2>
      <div class="mb-6 rounded border border-border p-3 dark:border-border-dark">
        <div v-if="seqNotProvisioned" class="text-sm text-fg-muted">
          Seq credentials not yet provisioned. Re-deploy or upgrade this playground to enable log access.
        </div>
        <div
          v-else-if="seqError"
          class="text-sm text-status-failed"
        >{{ seqError }}</div>
        <dl
          v-else
          class="grid grid-cols-1 gap-x-8 gap-y-1 text-sm sm:grid-cols-[max-content_1fr]"
        >
          <dt class="text-2xs uppercase tracking-wide text-fg-muted">URL</dt>
          <dd>
            <a
              v-if="seqCreds || playground.status === 'ready'"
              :href="seqCreds?.url ?? `https://${playground.customer}-seq.${playground.dnsZone}`"
              target="_blank"
              rel="noreferrer noopener"
              class="mono inline-flex items-center gap-1 underline"
            >
              {{ seqCreds?.url ?? `https://${playground.customer}-seq.${playground.dnsZone}` }}
              <ExternalLink :size="12" />
            </a>
            <span v-else class="mono text-fg-muted">
              https://{{ playground.customer }}-seq.{{ playground.dnsZone }}
              <span class="ml-1 text-2xs">(not yet reachable)</span>
            </span>
          </dd>

          <dt class="text-2xs uppercase tracking-wide text-fg-muted">Username</dt>
          <dd class="mono">admin</dd>

          <dt class="text-2xs uppercase tracking-wide text-fg-muted">Password</dt>
          <dd class="flex flex-wrap items-center gap-2">
            <span
              v-if="seqPasswordRevealed && seqCreds"
              class="mono break-all"
            >{{ seqCreds.password }}</span>
            <span v-else class="mono text-fg-muted">••••••••••••</span>
            <button
              v-if="!seqPasswordRevealed"
              type="button"
              class="rounded border border-border px-2 py-0.5 text-2xs hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark disabled:opacity-50"
              :disabled="seqLoading"
              @click="revealSeqPassword"
            >{{ seqLoading ? "Loading…" : "Reveal" }}</button>
            <button
              v-else
              type="button"
              class="rounded border border-border px-2 py-0.5 text-2xs hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark"
              @click="hideSeqPassword"
            >Hide</button>
            <button
              type="button"
              class="rounded border border-border px-2 py-0.5 text-2xs hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark disabled:opacity-50"
              :disabled="seqLoading"
              @click="copySeqPassword"
            >Copy</button>
          </dd>
        </dl>
      </div>

      <div class="mb-2 flex items-center justify-between">
        <h2 class="text-2xs font-medium uppercase tracking-wide text-fg-muted">Logs</h2>
        <button
          v-if="playground.lastJob?.id"
          type="button"
          class="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-2xs hover:bg-surface disabled:opacity-50 dark:border-border-dark dark:hover:bg-surface-dark"
          :disabled="downloadingLog"
          title="Download the full job log as a .log file"
          @click="downloadLog"
        >{{ downloadingLog ? "Downloading…" : "Download full log" }}</button>
      </div>
      <LogViewer :lines="logLines" />
    </template>

    <Dialog
      :open="deleteModalOpen"
      :title="`Delete playground &quot;${playground?.customer ?? ''}&quot;?`"
      @close="deleteModalOpen = false"
    >
      <p class="text-sm text-fg dark:text-fg-dark">This runs the orchestrator destroy flow. It removes:</p>
      <ul class="mt-2 list-disc pl-5 text-sm text-fg-muted">
        <li>The <span class="mono">{{ playground?.customer }}</span> resource group and all resources</li>
        <li>DNS records for <span class="mono">{{ playground?.fqdn }}</span></li>
        <li>The OIDC secret in shared Key Vault</li>
      </ul>
      <p class="mt-2 text-sm text-fg-muted">Takes ~5 minutes. Cannot be undone.</p>

      <div
        v-if="isAdminOverride"
        class="mt-3 rounded border border-status-provisioning/40 bg-status-provisioning/10 p-2 text-2xs text-status-provisioning"
      >
        Admin override: this playground was created by <span class="mono">{{ playground?.createdBy.upn }}</span>.
      </div>

      <label class="mt-4 block text-2xs text-fg dark:text-fg-dark">Type the customer slug to confirm:</label>
      <input
        v-model="deleteConfirm"
        type="text"
        autocomplete="off"
        class="mono mt-1 h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
      />

      <div class="mt-5 flex justify-end gap-2">
        <button
          type="button"
          class="rounded border border-border px-3 py-1.5 text-sm hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark"
          @click="deleteModalOpen = false"
        >Cancel</button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded bg-status-failed px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="deleteConfirm !== playground?.customer || deleting"
          @click="confirmDelete"
        ><Spinner v-if="deleting" :size="14" /> Delete</button>
      </div>
    </Dialog>

    <Dialog
      :open="showUpgradeDialog"
      :title="`Upgrade playground &quot;${playground?.customer ?? ''}&quot;`"
      @close="closeUpgradeDialog"
    >
      <p class="text-sm text-fg dark:text-fg-dark">
        Re-runs the orchestrator in <span class="mono">mode=upgrade</span>. Customer data,
        document repositories and Event Engine config customisations are preserved.
      </p>

      <div class="mt-3 text-sm">
        <div class="text-fg-muted">Current:</div>
        <div class="mono">
          ring <span>{{ playground?.ring ?? '—' }}</span>
          · sharedo <span>{{ playground?.sharedoVersion ?? '—' }}</span>
          · ee <span>{{ playground?.eeVersion ?? '—' }}</span>
        </div>
      </div>

      <!-- Target tier (release instances only). "Release version" is the
           normal version bump; "Playground build" re-platforms this release
           instance onto an experimental build (server allows release →
           playground only). -->
      <div v-if="!upgradeIsPlayground" class="mt-4">
        <label class="block text-2xs text-fg dark:text-fg-dark">Upgrade target:</label>
        <select
          v-model="upgradeTargetTier"
          class="mt-1 h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark"
          @change="onUpgradeTierChange"
        >
          <option value="release">Release version</option>
          <option value="playground">Playground build (experimental)</option>
        </select>
      </div>

      <label
        v-if="!crossTierToPlayground"
        class="mt-4 inline-flex items-center gap-2 text-sm"
      >
        <input v-model="upgradeRefresh" type="checkbox" />
        Refresh same {{ upgradeIsPlayground ? 'build' : 'version' }} (re-apply orchestrator changes without bumping)
      </label>

      <p v-if="upgradeIsPlayground && upgradeRefresh" class="mt-2 text-2xs text-fg-muted">
        Playground builds publish in place under their name — refresh re-pulls the
        latest content of <span class="mono">{{ playground?.sharedoVersion }}</span>.
        Uncheck to switch this instance to a different playground build.
      </p>

      <p
        v-if="crossTierToPlayground || (upgradeIsPlayground && !upgradeRefresh)"
        class="mt-3 rounded border border-status-provisioning/40 bg-status-provisioning/10 px-2 py-1.5 text-2xs text-fg dark:text-fg-dark"
      >
        Switching to a different playground build. Customer data is preserved, but
        the build's schema/migrations must be compatible with the existing database
        — that's your responsibility, and it can't be rolled back via downgrade.
      </p>

      <div v-if="!upgradeRefresh" class="mt-3">
        <label class="block text-2xs text-fg dark:text-fg-dark">{{ targetIsPlaygroundBuild ? 'Target build:' : 'Target version:' }}</label>
        <select
          v-model="upgradeTargetVersion"
          class="mt-1 h-8 w-full rounded border border-border bg-bg px-2 text-sm dark:border-border-dark dark:bg-surface-dark disabled:opacity-50"
          :disabled="displayedCandidatesLoading || displayedUpgradeCandidates.length === 0"
        >
          <option value="" disabled>{{ displayedCandidatesLoading ? 'Loading…' : (displayedUpgradeCandidates.length === 0 ? (targetIsPlaygroundBuild ? 'No playground builds available' : 'No newer versions available') : (targetIsPlaygroundBuild ? 'Select a build' : 'Select a version')) }}</option>
          <option
            v-for="v in displayedUpgradeCandidates"
            :key="v.version"
            :value="v.version"
            :disabled="!crossTierToPlayground && v.version === (playground?.sharedoVersion ?? '')"
          >
            {{ v.version }}{{ !crossTierToPlayground && v.version === (playground?.sharedoVersion ?? '') ? ' (current)' : '' }}
          </option>
        </select>
        <p v-if="!targetIsPlaygroundBuild" class="mt-1 text-2xs text-fg-muted">
          Versions older than the current build are hidden — downgrades aren't
          supported (DbUp's journal can't roll back schema changes).
        </p>
      </div>

      <div class="mt-5 flex justify-end gap-2">
        <button
          type="button"
          class="rounded border border-border px-3 py-1.5 text-sm hover:bg-surface dark:border-border-dark dark:hover:bg-surface-dark"
          @click="closeUpgradeDialog"
        >Cancel</button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded bg-status-provisioning px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="upgrading || (!upgradeRefresh && !upgradeTargetVersion)"
          @click="confirmUpgrade"
        ><Spinner v-if="upgrading" :size="14" /> Upgrade</button>
      </div>
    </Dialog>
  </section>
</template>
